/**
 * The editor's command-effect boundary: machine commands in, editor UX out.
 *
 * Registered by the editor plugin's persistent effect host for the state-machine cohort. The
 * Confluence lifecycle adapter starts admitted flows with `useCommandBus`, so commands reach this
 * hook through the process-default in-memory dispatcher.
 *
 * What this owns is the *policy* — which flow a command belongs to, whether its effect has already
 * run, and when the latched terminal read session may be dismissed. The product effects themselves
 * are injected, because they are editor UX (experience settlement, preview close, sidebar) that this
 * product-neutral package must not know about.
 *
 * ## Command order is not the order you would guess
 *
 * `@atlassian/state-machine` fires transition listeners *before* a state's `onEnter` effects. The
 * lifecycle observer in `editorRemixLifecycleOwner` runs on the transition and releases machine
 * ownership, which tears the flow down and announces `RemixFlowEnded`; the commands for the state
 * just entered are dispatched afterwards. So a terminal phase arrives as:
 *
 *     RemixFlowEnded('error')   →  RemixErrored  →  RemixGenerationEnded('error')
 *     RemixFlowEnded('aborted') →  RemixAborted  →  RemixGenerationEnded('aborted')
 *
 * `RemixFlowEnded` is therefore NOT last for a terminal outcome, and two rules follow:
 *
 *  1. The claim set is reset on session replacement only, never on `RemixFlowEnded` — clearing it
 *     there would wipe the claims immediately *before* the terminal UX commands arrive, so a
 *     repeated `RemixErrored` would run error cleanup twice.
 *  2. `RemixFlowEnded('error' | 'aborted')` must retain the session. The paired `RemixErrored` /
 *     `RemixAborted` cleanup dismisses it, and only once that cleanup has run.
 *
 * Dismissal is still ordering-safe: the observer writes the latched phase into the provider's ref
 * synchronously before releasing, and `dismissTerminal` reads that ref rather than rendered state.
 * A cleanup handler that dismisses at the end of the same synchronous turn therefore sees the latch,
 * even though React has not re-rendered the session yet.
 *
 * ## Mount this at the persistent owner level, not inside the Remix content component
 *
 * `RemixContentComponent` renders nothing unless the modal, thinking UI or location picker is up, so
 * terminal cleanup unmounts it — and unmounting drops this hook's subscription along with it, losing
 * the dismissal and every command that follows. It has to be mounted somewhere that outlives a
 * settled flow, inside `EditorRemixLifecycleOwnerProvider` but above that content component.
 *
 * The practical consequence: the editor UX closures that live in `RemixContentComponentInner`
 * (`onStopThinking`, `handleModalClose`, the preview open/close pair, the experience tracker) are
 * not in scope at that level, so the host reaches them through the plugin's api-keyed callback
 * registry, read at invocation time rather than captured at mount.
 *
 * ## A safety/AUP violation is not a command, and must never become one
 *
 * A safety or AUP violation arrives as a *completed* action while the friendly response is still
 * streaming into the conversation, so the editor handles it locally off the AIFC store signal:
 * dismiss the thinking UI, close any preview, open the side chat. There is deliberately no command
 * for it here. Mapping it onto `RemixErrored` would run error cleanup — which aborts the in-flight
 * request — and cut off the friendly response before the user ever sees it.
 *
 * ## `onAborted` is origin-blind, deliberately
 *
 * `CANCELLED` transitions the machine to `aborted`, so the observer's `end('aborted')` pre-empts
 * every producer `end('cancelled')`. In treatment a local cancel, a Rovo-side abort and a preview
 * dismissal all arrive as the same `RemixAborted`. A caller that needs to tell them apart records
 * its own intent before driving the machine and reads it inside `onAborted`, the way the renderer
 * surface does — this hook cannot infer it from the command.
 */

import { useRef } from 'react';

import type {
	RemixCommand,
	RemixFlowEndReason,
} from '../../state/machine/commands/remixCommandBus';
import { useRemixCommands } from '../../state/machine/commands/useRemixCommands';

import { useEditorRemixLifecycle } from './editorRemixLifecycleOwner';

type RemixReadyCommand = Extract<RemixCommand, { type: 'RemixReady' }>;
type RemixErroredCommand = Extract<RemixCommand, { type: 'RemixErrored' }>;
type RemixAbortedCommand = Extract<RemixCommand, { type: 'RemixAborted' }>;
type RemixFlowEndedCommand = Extract<RemixCommand, { type: 'RemixFlowEnded' }>;

/**
 * One claim per user-visible effect, scoped to a single lifecycle session.
 *
 * Only commands that own an effect are claimed. `RemixFlowEnded` is keyed by reason because the
 * reasons are mutually exclusive outcomes, and keying on the bare type would let a stray second
 * teardown announcement of a *different* reason be silently swallowed.
 */
type EditorRemixCommandClaim =
	| 'RemixReady'
	| 'RemixErrored'
	| 'RemixAborted'
	| `RemixFlowEnded:${RemixFlowEndReason}`;

export type EditorRemixCommandHandlers = {
	/** Marks completion and starts the local completion animation. */
	onReady: (command: RemixReadyCommand) => void;
	/**
	 * Owns failure settlement: experience failure, error cleanup, preview close, sidebar open.
	 *
	 * Must complete its modal close and sidebar opening synchronously — the latched terminal session
	 * is dismissed as soon as this returns.
	 */
	onErrored: (command: RemixErroredCommand) => void;
	/**
	 * Owns abort settlement: experience abort and thinking cleanup.
	 *
	 * Must complete its thinking cleanup synchronously, for the same reason as {@link onErrored}.
	 * Origin-blind — see the module comment.
	 */
	onAborted: (command: RemixAbortedCommand) => void;
	/**
	 * Clears editor-owned presentation after an acknowledged insertion settles the flow.
	 *
	 * The terminal flow-end command is the only insertion signal that reaches the persistent
	 * editor effect host. It is dispatched only after the producer has accepted `INSERTED`.
	 */
	onInserted?: (command: RemixFlowEndedCommand) => void;
	/**
	 * Cancellation cleanup, run before a `cancelled` teardown clears the session.
	 *
	 * Fires only when a `cancelled` teardown arrives with no terminal command having settled the
	 * session first. Do not plan treatment cancellation UX around it: every current producer sends
	 * `CANCELLED` before ending, which drives the machine to `aborted`, so cancellation reaches
	 * `onAborted` instead and this yields to it. It exists for a `cancelled` teardown of a flow that
	 * never reached a terminal phase at all.
	 */
	onCancellationSettled?: (command: RemixFlowEndedCommand) => void;
	/**
	 * Reports a handler that threw. Not a `console.error`: error reporting for this migration is the
	 * product's to choose, so this package must not pick one.
	 *
	 * Required, because every injected effect runs inside a `try/catch` so a throwing one cannot skip
	 * the dismissal that follows it. That makes this the only place a failure can surface, and an
	 * omitted reporter would silently swallow a half-completed terminal cleanup.
	 */
	onHandlerError: (info: { commandType: RemixCommand['type']; error: unknown }) => void;
};

/** End reasons whose read session must survive teardown until terminal UX dismisses it. */
const isTerminalFlowEndReason = (reason: RemixFlowEndReason): boolean =>
	reason === 'error' || reason === 'aborted';

/**
 * Owns editor product effects for one admitted lifecycle session.
 *
 * The default dispatcher fans every command out process-wide, so ownership is established by the
 * session's exact `remixId`: an unrelated, stale or replaced flow has no effect here, and cannot
 * dismiss a newer session.
 */
export const useEditorRemixCommands = ({
	onReady,
	onErrored,
	onAborted,
	onInserted,
	onCancellationSettled,
	onHandlerError,
}: EditorRemixCommandHandlers): void => {
	const { remixId: sessionRemixId, dismissTerminal } = useEditorRemixLifecycle();
	const claimedRef = useRef<{
		remixId: string | undefined;
		claims: Set<EditorRemixCommandClaim>;
		/** Set once a teardown reason has cleared this session. Nothing may act on it afterwards. */
		isCleared: boolean;
	}>({ remixId: undefined, claims: new Set(), isCleared: false });

	useRemixCommands((command) => {
		// Session identity alone, never `isAdmitted`: admission is already cleared by the time the
		// terminal commands arrive.
		if (sessionRemixId === undefined || command.remixId !== sessionRemixId) {
			return;
		}

		// The only reset. Clearing on `RemixFlowEnded` would drop the claims just before the terminal
		// commands arrive — see the module comment on ordering.
		if (claimedRef.current.remixId !== command.remixId) {
			claimedRef.current = { remixId: command.remixId, claims: new Set(), isCleared: false };
		}

		// Suppress same-turn commands until the cleared session is rendered.
		if (claimedRef.current.isCleared) {
			return;
		}

		const claimOnce = (claim: EditorRemixCommandClaim): boolean => {
			if (claimedRef.current.claims.has(claim)) {
				return false;
			}
			claimedRef.current.claims.add(claim);
			return true;
		};

		/** Whether a terminal command has already settled this session's cleanup UX. */
		const hasSettledTerminalCleanup = (): boolean =>
			claimedRef.current.claims.has('RemixErrored') ||
			claimedRef.current.claims.has('RemixAborted');

		/**
		 * Isolates the injected effect from the dismissal that follows it: an unwrapped throw in
		 * `onErrored` would skip `dismissTerminal()` and strand the latched session forever.
		 */
		const runEffect = (effect: () => void): void => {
			try {
				effect();
			} catch (error) {
				try {
					onHandlerError({ commandType: command.type, error });
				} catch {
					// Diagnostics must never affect command handling.
				}
			}
		};

		switch (command.type) {
			// Effect-free on this surface, so not claimed either.
			case 'RemixStarted':
			case 'RemixGenerationEnded':
				return;

			case 'RemixReady':
				if (claimOnce('RemixReady')) {
					runEffect(() => onReady(command));
				}
				return;

			case 'RemixErrored':
				if (claimOnce('RemixErrored')) {
					runEffect(() => onErrored(command));
					dismissTerminal();
				}
				return;

			case 'RemixAborted':
				if (claimOnce('RemixAborted')) {
					runEffect(() => onAborted(command));
					dismissTerminal();
				}
				return;

			case 'RemixFlowEnded': {
				if (!claimOnce(`RemixFlowEnded:${command.reason}`)) {
					return;
				}
				// Retained, not cleared: the paired terminal command arrives after this one and still
				// owns the cleanup and the dismissal.
				if (isTerminalFlowEndReason(command.reason)) {
					return;
				}
				if (command.reason === 'inserted') {
					runEffect(() => onInserted?.(command));
				}
				claimedRef.current.isCleared = true;
				// A rollback releases ownership before sending `CANCELLED`, so the machine reaches
				// `aborted` unobserved and `RemixAborted` lands before this. Abort ownership wins;
				// running cancellation cleanup on top would settle the same experience twice.
				if (command.reason === 'cancelled' && !hasSettledTerminalCleanup()) {
					// Relies on React batching to retain sessionRemixId for this command turn.
					runEffect(() => onCancellationSettled?.(command));
				}
				return;
			}
		}
	});
};
